import React, { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import { Box, Button, Slider, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { fmtShortDateWithYear } from './utils';

function clampDateIndexRange(range: readonly [number, number], maxIndex: number): [number, number] {
  const cap = Math.max(0, maxIndex);
  let lo = Math.max(0, Math.min(range[0], cap));
  let hi = Math.max(0, Math.min(range[1], cap));
  if (lo > hi) {
    [lo, hi] = [hi, lo];
  }
  return [lo, hi];
}

/** Binary-search for the index of `target` (YYYY-MM-DD) in a sorted dates array. Clamps to bounds. */
function findDateIndex(dates: Array<string>, target: string): number {
  let lo = 0;
  let hi = dates.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (dates[mid] < target) {
      lo = mid + 1;
    } else if (dates[mid] > target) {
      hi = mid - 1;
    } else {
      return mid;
    }
  }
  // `lo` is the insertion point — clamp to valid range
  return Math.max(0, Math.min(lo, dates.length - 1));
}

interface Props {
  dates: Array<string>;
  range: [number, number];
  onRangeChange: (range: Array<number>) => void;
  onRangeCommit?: (range: Array<number>) => void;
  /** Show quick-select buttons and date pickers below the slider. */
  showQuickSelects?: boolean;
}

export default function DateRangeSlider({ dates, range, onRangeChange, onRangeCommit, showQuickSelects }: Props) {
  const max = Math.max(0, dates.length - 1);

  // Internal state drives the slider visuals without re-rendering the parent during drag.
  const [localRange, setLocalRange] = useState<[number, number]>(() => clampDateIndexRange(range, max));

  // Sync internal state when the parent changes the range prop (e.g. quick-select, reset).
  useEffect(() => {
    setLocalRange(clampDateIndexRange(range, max));
  }, [range, max]);

  const value = useMemo(() => clampDateIndexRange(localRange, max), [localRange, max]);

  const minDate = useMemo(() => (dates[0] ? dayjs(dates[0]) : null), [dates]);
  const maxDate = useMemo(() => (dates[max] ? dayjs(dates[max]) : null), [dates, max]);

  const commit = useCallback(
    (clamped: [number, number]) => {
      setLocalRange(clamped);
      onRangeChange(clamped);
      onRangeCommit?.(clamped);
    },
    [onRangeChange, onRangeCommit],
  );

  const applyRange = useCallback(
    (fromIdx: number, toIdx: number) => {
      commit(clampDateIndexRange([fromIdx, toIdx], max));
    },
    [max, commit],
  );

  const handleQuickSelect = useCallback(
    (getFromDate: () => string) => {
      const fromDate = getFromDate();
      const fromIdx = findDateIndex(dates, fromDate);
      applyRange(fromIdx, max);
    },
    [dates, max, applyRange],
  );

  const handleFromDateChange = useCallback(
    (d: dayjs.Dayjs | null) => {
      if (!d?.isValid()) {
        return;
      }
      const fromIdx = findDateIndex(dates, d.format('YYYY-MM-DD'));
      applyRange(fromIdx, value[1]);
    },
    [dates, value, applyRange],
  );

  const handleToDateChange = useCallback(
    (d: dayjs.Dayjs | null) => {
      if (!d?.isValid()) {
        return;
      }
      const toIdx = findDateIndex(dates, d.format('YYYY-MM-DD'));
      applyRange(value[0], toIdx);
    },
    [dates, value, applyRange],
  );

  if (dates.length === 0) {
    return null;
  }

  const today = dayjs();

  return (
    <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: '12px', border: 1, borderColor: 'divider', mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>Date Range</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'primary.main' }}>
          {fmtShortDateWithYear(dates[value[0]] ?? '')} - {fmtShortDateWithYear(dates[value[1]] ?? '')}
        </Typography>
      </Box>
      <Slider
        value={value}
        onChange={(_, val) => setLocalRange(val as [number, number])}
        onChangeCommitted={(_, val) => {
          const clamped = clampDateIndexRange(val as [number, number], max);
          onRangeChange(clamped);
          onRangeCommit?.(clamped);
        }}
        min={0}
        max={max}
        color="primary"
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{fmtShortDateWithYear(dates[0] ?? '')}</Typography>
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{fmtShortDateWithYear(dates[max] ?? '')}</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 2 }}>
        {showQuickSelects && (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleQuickSelect(() => today.startOf('month').format('YYYY-MM-DD'))}
            >
              Calendar MTD
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleQuickSelect(() => today.startOf('year').format('YYYY-MM-DD'))}
            >
              Calendar YTD
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const july1 = today.month(6).date(1).startOf('day');
                const fyStart = today.isBefore(july1) ? july1.subtract(1, 'year') : july1;
                handleQuickSelect(() => fyStart.format('YYYY-MM-DD'));
              }}
            >
              Financial YTD
            </Button>
          </Stack>
        )}

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', ml: 'auto' }}>
          <DatePicker
            label="From"
            value={dates[value[0]] ? dayjs(dates[value[0]]) : null}
            onChange={handleFromDateChange}
            minDate={minDate ?? undefined}
            maxDate={maxDate ?? undefined}
            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
          />
          <Typography sx={{ color: 'text.secondary' }}>–</Typography>
          <DatePicker
            label="To"
            value={dates[value[1]] ? dayjs(dates[value[1]]) : null}
            onChange={handleToDateChange}
            minDate={minDate ?? undefined}
            maxDate={maxDate ?? undefined}
            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
          />
        </Stack>
      </Box>
    </Box>
  );
}
